import { forwardRef, memo, useId } from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { cva, VariantProps } from 'class-variance-authority';
import { CheckSmallIcon, MinusIcon } from '@page/icons';

import { cn } from '../../lib/styles';
import { Label } from '../Label';

const checkboxVariants = cva('', {
  variants: {
    variant: {
      default: '',
      secondary: [
        'py-2 px-2.5 rounded-sm',
        'bg-bg-inter-secondary-default hover:bg-bg-inter-secondary-hover active:bg-bg-inter-secondary-pressed',
        'disabled:bg-bg-inter-secondary-disabled',
      ],
    },
    size: {
      sm: '',
      md: 'py-2.5 px-2.5',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export interface CheckboxProps
  extends React.ComponentProps<typeof CheckboxPrimitive.Root>,
    VariantProps<typeof checkboxVariants> {
  indeterminate?: boolean;
  label?: React.ReactNode;
  description?: React.ReactNode;
  labelClassName?: string;
  checkboxClassName?: string;
  alwaysShowError?: boolean;
  error?: React.ReactNode;
  readonly?: boolean;
}

const CheckboxBase = (
  {
    id: idFromProps,
    className,
    variant,
    label,
    description,
    labelClassName,
    checked,
    indeterminate,
    disabled,
    alwaysShowError = false,
    error,
    checkboxClassName,
    size,
    readonly,
    ...rest
  }: CheckboxProps,
  ref: React.Ref<HTMLButtonElement>,
) => {
  const randomId = useId();
  const id = idFromProps ?? randomId;

  const checkboxButton = (
    <CheckboxPrimitive.Root
      ref={ref}
      id={id}
      data-slot="checkbox"
      className={cn(
        'group peer rounded-[4px] border',
        'size-4 shrink-0 transition-all focus-visible:outline-2',
        'cursor-pointer enabled:hover:border-bg-inter-primary-active enabled:group-hover:border-bg-inter-primary-active',
        'disabled:cursor-not-allowed disabled:bg-bg-inter-primary-disabled/35 bg-bg-sur-primary',
        {
          'enabled:border-bg-inter-primary-active enabled:bg-bg-inter-primary-default': indeterminate,
          'border-border-secondary enabled:data-[state=checked]:border-bg-inter-primary-active enabled:data-[state=checked]:bg-bg-inter-primary-default enabled:data-[state=checked]:text-white':
            !indeterminate,
        },
        {
          'pointer-events-none': readonly,
        },
        label ? checkboxClassName : checkboxClassName || className,
      )}
      checked={indeterminate ? undefined : checked}
      disabled={disabled}
      {...rest}
    >
      {indeterminate ? (
        <div className="flex items-center justify-center group-enabled:text-white group-disabled:text-icon-disabled transition-none relative">
          <MinusIcon className="size-5 absolute" fill="currentColor" />
        </div>
      ) : (
        <CheckboxPrimitive.Indicator
          data-slot="checkbox-indicator"
          className="flex items-center justify-center group-enabled:text-white group-disabled:text-icon-disabled transition-none relative"
        >
          <CheckSmallIcon className="size-5 absolute" fill="currentColor" />
        </CheckboxPrimitive.Indicator>
      )}
    </CheckboxPrimitive.Root>
  );

  if (!label) return checkboxButton;

  return (
    <div className={cn('flex flex-col', className)}>
      <div
        className={cn('flex gap-2 w-full', checkboxVariants({ variant, size }), {
          '[&_*]:cursor-not-allowed': disabled,
        })}
      >
        <div className="flex items-center">{checkboxButton}</div>

        <Label
          htmlFor={id}
          className={cn(
            'font-body-md font-semibold',
            {
              'text-text-primary': !disabled,
              'text-text-disabled': disabled,
              'flex flex-col items-start gap-0 leading-4!': !!description,
            },
            labelClassName,
          )}
        >
          {description ? <span className="truncate w-full">{label}</span> : label}

          {description ? (
            <span className={cn('font-body-xs text-text-tertiary font-normal', { 'text-text-disabled': disabled })}>
              {description}
            </span>
          ) : null}
        </Label>
      </div>

      {alwaysShowError || error ? (
        <div data-slot="error" className="h-5 pt-1 text-text-destructive font-body-sm">
          {error}
        </div>
      ) : null}
    </div>
  );
};

export const Checkbox = memo(forwardRef(CheckboxBase));
